
import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

const ElsiesRiverPage: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for Elsies River page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Elsies River Painters",
      "description": "Professional painting contractors in Elsies River, Western Cape. Get quotes for interior, exterior, roof and commercial painting services.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/elsies-river-painters`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Elsies River",
        "addressRegion": "Western Cape",
        "addressCountry": "ZA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-33.8847",
        "longitude": "18.5489"
      },
      "serviceArea": {
        "@type": "City",
        "name": "Elsies River"
      },
      "services": [
        "Residential Interior Painting",
        "Exterior House Painting",
        "Roof Painting",
        "Commercial Painting",
        "Community Painting Projects",
        "Surface Preparation"
      ]
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Elsies River Painters - Community Painting Contractors Western Cape | Free Quotes";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Community-focused painting contractors in Elsies River, Western Cape. Get free quotes for interior, exterior, roof and commercial painting. Local expertise, affordable solutions.');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[70vh] flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/0ec42ac5320bb8dd4159d6cc9d7254f4.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="block">Elsies River</span>
            <span className="block text-[#e9f638]">PAINTERS</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Get quotes from the best painting contractors in Elsies River
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Professional Painting Services in Elsies River
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Discover trusted painting contractors who understand the vibrant community spirit of Elsies River. Our network connects you with skilled professionals who bring both technical expertise and genuine care for this dynamic neighborhood in Cape Town's Northern Suburbs.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our Elsies River painting specialists are committed to delivering exceptional results that enhance property values while respecting the community's diverse character. From family homes to local businesses, each project is approached with the professionalism and attention to detail that Elsies River residents deserve.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Whether you're updating your family home, refreshing a rental property, or maintaining a business in Elsies River's commercial areas, our contractors understand the local market and community needs, delivering quality workmanship that stands the test of time while fitting your budget.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Community-Focused Service</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Local Market Knowledge</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Quality Workmanship</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] text-xl mr-3"></i>
                  <span className="text-gray-700">Affordable Solutions</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20community%20homes%20in%20Elsies%20River%20with%20vibrant%20neighborhood%20setting%20and%20diverse%20architectural%20styles%2C%20quality%20painting%20equipment%2C%20clean%20professional%20appearance%2C%20bright%20natural%20lighting&width=600&height=400&seq=elsies-river-painting-work&orientation=landscape"
                alt="Professional painters working in Elsies River"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Elsies River Community */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
            Understanding Elsies River's Community Spirit and Painting Requirements
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-600 mb-6">
                Elsies River embodies the strength and resilience of Cape Town's communities, with a rich cultural heritage and strong neighborhood bonds that influence every aspect of local life, including home improvement and property maintenance. Our painting contractors understand that working in Elsies River means becoming part of a community that values quality, reliability, and mutual respect.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The diverse housing stock in Elsies River presents unique opportunities for painting contractors who understand both traditional building methods and modern renovation techniques. From older homes with character features to newer developments, each property type requires specialized knowledge and a commitment to enhancing the neighborhood's overall appeal.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Elsies River's location and community dynamics create specific considerations for painting projects, from understanding local preferences and cultural sensitivities to working within budget constraints while maintaining high quality standards. Our network of contractors brings this essential local knowledge to every project.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Elsies%20River%20South%20Africa%20community%20neighborhood%20with%20diverse%20housing%20styles%2C%20local%20businesses%2C%20community%20atmosphere%2C%20residential%20streets%2C%20vibrant%20neighborhood%20character%2C%20painting%20work%20in%20progress&width=600&height=400&seq=elsies-river-community&orientation=landscape"
                alt="Elsies River community neighborhood"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Elsies River Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Elsies River Painting Services
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Community-focused painting solutions that enhance Elsies River properties while respecting local values and budgets
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-4-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Elsies River Family Home Painting</h5>
              <p className="text-gray-600">Specialized painting services for Elsies River family homes, understanding the importance of creating beautiful, comfortable living spaces that reflect community pride and family values.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Elsies River Exterior Painting</h5>
              <p className="text-gray-600">Professional exterior painting that enhances Elsies River's neighborhood character, using durable materials and techniques suited to the local climate and community standards.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-home-2-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Elsies River Interior Transformation</h5>
              <p className="text-gray-600">Interior painting services that transform Elsies River homes into vibrant, welcoming spaces, with color consultation and finish options that reflect personal style and community culture.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-office-building-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Elsies River Commercial Painting</h5>
              <p className="text-gray-600">Professional painting for Elsies River's local businesses and commercial properties, supporting economic development with quality, affordable solutions that attract customers and enhance business appeal.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-brush-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Elsies River Community Projects</h5>
              <p className="text-gray-600">Specialized painting services for community centers, schools, and public spaces in Elsies River, contributing to neighborhood improvement and community pride through quality workmanship.</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg border">
              <div className="w-12 h-12 bg-[#e9f638] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tools-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-3">Elsies River Maintenance Solutions</h5>
              <p className="text-gray-600">Regular maintenance painting services that help Elsies River property owners protect their investments and maintain community standards with affordable, reliable solutions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Elsies River Painters */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Elsies River Painting Network
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Connecting you with Elsies River's most trusted and community-minded painting professionals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Elsies River Community Understanding</h5>
              <p className="text-gray-600">Our contractors understand Elsies River's unique community character, cultural diversity, and local preferences, ensuring painting solutions that truly fit the neighborhood.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Elsies River Quality Commitment</h5>
              <p className="text-gray-600">All our Elsies River painters are committed to delivering quality workmanship that enhances property values and contributes to the community's continued growth and improvement.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-time-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Elsies River Reliable Service</h5>
              <p className="text-gray-600">Dependable contractors who understand the importance of reliability and trust in the Elsies River community, completing projects on time and building lasting relationships.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-black"></i>
              </div>
              <h5 className="text-xl font-semibold text-gray-900 mb-4">Elsies River Fair Pricing</h5>
              <p className="text-gray-600">Transparent, community-friendly pricing that ensures quality painting services remain accessible to Elsies River residents and business owners at every budget level.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Elsies River Area Coverage */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
            Complete Elsies River Area Coverage
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Elsies River Residential Areas</h6>
              <p className="text-gray-600">Comprehensive painting services for all Elsies River residential neighborhoods, from established family areas to developing communities and everything in between.</p>
            </div>
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Elsies River Commercial District</h6>
              <p className="text-gray-600">Professional painting for Elsies River's business areas, supporting local economic development with quality, affordable painting solutions for shops, offices, and service businesses.</p>
            </div>
            <div className="text-center p-6">
              <h6 className="text-xl font-semibold text-gray-900 mb-3">Greater Elsies River Area</h6>
              <p className="text-gray-600">Extended coverage throughout the broader Elsies River region, including surrounding neighborhoods and community facilities that benefit from our local expertise and commitment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Your Free Elsies River Painting Quotes
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to enhance your Elsies River property? Connect with trusted painting contractors who understand your community and offer fair, transparent pricing
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Elsies River Residents Say
            </h4>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Elsies River homeowners and business owners who found their perfect painting contractor through our trusted community network
            </p>
          </div>
          <TestimonialsSlider />
        </div>
      </section>

      <Footer />
      
      {/* Elfsight AI Chatbot */}
      <div>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>
      </div>
    </div>
  );
};

export default ElsiesRiverPage;
